'use client'
import React, { useReducer, useEffect, useCallback } from 'react'
import { useLingui } from '@lingui/react'
import { useSession } from '@/shared/ui/providers/AuthSessionProvider'

// Import from our modular files
import { BookingState, BookingStepEnum, BookingContextValue } from '../model/bookingTypes'
import { bookingReducer } from '../model/bookingReducer'
import {
  determineNextStep,
  determinePreviousStep,
  isStepCompleted,
  isStepAccessible,
  getStepUrl as getStepUrlUtil,
  getVisibleSteps as getVisibleStepsUtil,
  createCustomizedStepOrder,
} from '../lib/bookingUtils'
import { saveStateToStorage, cleanupOldSessions } from '../lib/bookingStorage'
import { BookingContext } from '../model/bookingContext'

// Provider Component
export function BookingProvider({ children, initialState }: { children: React.ReactNode; initialState: BookingState }) {
  const { data: session, status } = useSession()
  const { i18n } = useLingui()
  const [state, dispatch] = useReducer(bookingReducer, initialState)

  const isLoggedIn = status === 'authenticated'
  const bookingLanguage = i18n.locale

  useEffect(() => {
    dispatch({ type: 'SET_IS_LOGGED_IN', payload: isLoggedIn })
  }, [dispatch, isLoggedIn])

  // Save state to storage when it changes
  useEffect(() => {
    saveStateToStorage(state)
  }, [state])

  // If user is logged in, pre-populate patient info from session
  // useEffect(() => {
  //   if (isLoggedIn && session?.user) {
  //     // Only update if we don't already have this info
  //     if (!state.patientInfo.firstName || !state.patientInfo.lastName || !state.patientInfo.email) {
  //       dispatch({
  //         type: 'SET_PATIENT_INFO',
  //         payload: {
  //           firstName: session.user.name?.split(' ')[0] || '',
  //           lastName: session.user.name?.split(' ').slice(1).join(' ') || '',
  //           email: session.user.email || '',
  //         },
  //       })
  //     }
  //   }
  // }, [isLoggedIn, session, state.patientInfo])

  // Step progression logic
  const goToNextStep = (nextState?: BookingState) => {
    const nextStep = determineNextStep(nextState ?? state, bookingLanguage)
    if (nextStep) {
      dispatch({ type: 'SET_STEP', payload: nextStep })
    }
  }

  const goToPreviousStep = (nextState?: BookingState) => {
    const prevStep = determinePreviousStep(nextState ?? state, bookingLanguage)
    if (prevStep) {
      dispatch({ type: 'SET_STEP', payload: prevStep })
    }
  }

  const goToStep = (step: BookingStepEnum) => {
    // Log navigation attempt for debugging
    console.log(`Attempting to navigate to step: ${step}`)

    // If trying to access current step, do nothing
    if (step === state.currentStep) {
      console.log(`Already on step: ${step}`)
      return
    }

    // Check if the step is accessible
    if (isStepAccessible(step, state, bookingLanguage)) {
      console.log(`Navigating to accessible step: ${step}`)
      dispatch({ type: 'SET_STEP', payload: step })
      // router.push(getStepUrl(step))
    } else {
      // Log that the step is not accessible
      console.log(`Step not accessible: ${step}`)

      // Get the customized step order
      const customizedStepOrder = createCustomizedStepOrder(state, bookingLanguage)

      // Find the first incomplete step
      let targetStep = BookingStepEnum.SPECIALITIES
      for (const currentStep of customizedStepOrder) {
        if (!isStepCompleted(currentStep, state)) {
          targetStep = currentStep
          break
        }
      }

      // Navigate to the first incomplete step
      console.log(`Redirecting to incomplete step: ${targetStep}`)
      dispatch({ type: 'SET_STEP', payload: targetStep })
    }
  }

  // Helper functions with state baked in
  const isStepCompletedWithState = (step: BookingStepEnum) => {
    return isStepCompleted(step, state)
  }

  const isStepAccessibleWithState = (step: BookingStepEnum) => {
    return isStepAccessible(step, state, bookingLanguage)
  }

  const getStepUrl = (step: BookingStepEnum): string => {
    return getStepUrlUtil(step, state.meetingLinkSlug)
  }

  const getVisibleSteps = useCallback(() => {
    return getVisibleStepsUtil(state, bookingLanguage)
  }, [bookingLanguage, state])

  const isLastActionableStep = useCallback(
    (step: BookingStepEnum) => {
      const visibleSteps = getVisibleStepsUtil(state, bookingLanguage)
      const actionableSteps = visibleSteps.filter((s) => s.key !== BookingStepEnum.CONFIRMATION)
      if (actionableSteps.length === 0) return false
      return actionableSteps[actionableSteps.length - 1].key === step
    },
    [bookingLanguage, state]
  )

  // Run the cleanup on mount and then every hour
  const cleanupCallback = useCallback(cleanupOldSessions, [])

  useEffect(() => {
    cleanupCallback()
    const intervalId = setInterval(cleanupCallback, 60 * 60 * 1000) // Every hour
    return () => clearInterval(intervalId)
  }, [cleanupCallback])

  // Add the recovery effect
  // useEffect(() => {
  //   // Skip this effect if isStepAccessible is not yet defined
  //   if (!isStepAccessible) return

  //   // Check if we have a meeting link slug from the URL
  //   const pathParts = typeof window !== 'undefined' ? window.location.pathname.split('/') : []
  //   const meetingLinkIndex = pathParts.findIndex((part) => part === 'meeting-links')

  //   if (meetingLinkIndex >= 0 && pathParts.length > meetingLinkIndex + 1) {
  //     const meetingLinkSlug = pathParts[meetingLinkIndex + 1]

  //     // Check if there's a specific step in the URL
  //     let targetStep: BookingStepEnum | null = null
  //     const bookingIndex = pathParts.indexOf('booking')
  //     if (bookingIndex >= 0 && pathParts.length > bookingIndex + 1) {
  //       const stepParam = pathParts[bookingIndex + 1]
  //       // Validate that it's a valid step
  //       if (Object.values(BookingStepEnum).includes(stepParam as BookingStepEnum)) {
  //         targetStep = stepParam as BookingStepEnum
  //       }
  //     }

  //     // Check URL parameters for recovery options
  //     const urlParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '')
  //     const forceNew = urlParams.get('new') === 'true'

  //     // Only attempt recovery if we don't already have this meeting link loaded and we're not forcing a new session
  //     if (meetingLinkSlug && (!state.meetingLinkSlug || state.meetingLinkSlug !== meetingLinkSlug) && !forceNew) {
  //       console.log(`Attempting to recover session for meeting link: ${meetingLinkSlug}`)

  //       // Try to load state specifically for this meeting link
  //       const backupKey = `bookingState_backup_${meetingLinkSlug}`
  //       const storedState = localStorage.getItem(backupKey)

  //       if (storedState) {
  //         try {
  //           const parsedState = JSON.parse(storedState)
  //           // Validate the stored state has necessary information
  //           if (parsedState && parsedState.meetingLinkSlug === meetingLinkSlug) {
  //             console.log('Found previous session, recovering state')

  //             // Check if state has expired
  //             if (parsedState._meta && new Date(parsedState._meta.expiresAt) < new Date()) {
  //               console.log('Session expired, starting fresh')
  //               dispatch({ type: 'SET_MEETING_LINK_SLUG', payload: meetingLinkSlug })

  //               // If a target step was specified
  //               if (targetStep) {
  //                 const isAccessible = isStepAccessible(targetStep, initialState)
  //                 if (isAccessible) {
  //                   dispatch({ type: 'SET_CURRENT_STEP', payload: targetStep })
  //                 }
  //               }
  //             } else {
  //               // Clear metadata and restore state
  //               const { _meta, ...cleanState } = parsedState
  //               dispatch({ type: 'RESTORE_STATE', payload: cleanState })

  //               // If a target step was specified
  //               if (targetStep) {
  //                 const isAccessible = isStepAccessible(targetStep, cleanState as BookingState)
  //                 if (isAccessible) {
  //                   dispatch({ type: 'SET_CURRENT_STEP', payload: targetStep })
  //                 }
  //               }

  //               // Show recovery notification
  //               if (typeof window !== 'undefined' && window.sessionStorage.getItem('showRecoveryNotification') !== 'false') {
  //                 window.sessionStorage.setItem('showRecoveryNotification', 'true')
  //               }
  //             }
  //           }
  //         } catch (error) {
  //           console.error('Error recovering session:', error)
  //         }
  //       } else {
  //         // No stored state found, just set the meeting link slug
  //         dispatch({ type: 'SET_MEETING_LINK_SLUG', payload: meetingLinkSlug })

  //         // If a target step was specified
  //         if (targetStep) {
  //           const newState = { ...state, meetingLinkSlug }
  //           const isAccessible = isStepAccessible(targetStep, newState)
  //           if (isAccessible) {
  //             dispatch({ type: 'SET_CURRENT_STEP', payload: targetStep })
  //           }
  //         }
  //       }
  //     } else if (forceNew && meetingLinkSlug) {
  //       // User explicitly requested a new session
  //       console.log('Starting fresh session as requested via URL parameter')
  //       dispatch({ type: 'RESET_STATE' })
  //       dispatch({ type: 'SET_MEETING_LINK_SLUG', payload: meetingLinkSlug })

  //       // If a target step was specified
  //       if (targetStep) {
  //         const newState = { ...initialState, meetingLinkSlug }
  //         const isAccessible = isStepAccessible(targetStep, newState)
  //         if (isAccessible) {
  //           dispatch({ type: 'SET_CURRENT_STEP', payload: targetStep })
  //         }
  //       }
  //     }
  //   }
  // }, [state.meetingLinkSlug, dispatch])

  // Create the context value
  const value: BookingContextValue = {
    state,
    dispatch,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    isStepCompleted: isStepCompletedWithState,
    saveStateToStorage: () => saveStateToStorage(state),
    getStepUrl,
    isStepAccessible: isStepAccessibleWithState,
    getVisibleSteps,
    isLastActionableStep,
  }

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
}
